import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import { Globe, Camera, Save, Loader2, X, Plus, Bot, PieChart } from 'lucide-react';
import { HeatmapLayer } from './HeatmapLayer'; 

import { AgentIncidentTracker } from './AgentIncidentTracker';
import { ParallelVoteTally } from './ParallelVoteTally';

import 'leaflet/dist/leaflet.css';
import { 
  Users, 
  Megaphone, 
  TrendingUp, 
  MessageSquare, 
  MapPin, 
  Heart,
  BarChart3,
  Share2,
  UserCircle,
  QrCode,
  Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext'; 
import { CampaignLinkGenerator } from './CampaignLinkGenerator';

interface Conversation {
  id: string;
  voter_phone: string;
  status: 'pending' | 'replied'; // or whatever statuses you use
  // add other properties that come from your API here
  [key: string]: any; 
}

interface PoliticalDashboardProps {
  onViewChange: (view: string) => void;
}

// --- PROJECT MODAL COMPONENT ---
function ProjectModal({ isOpen, onClose, shopId }: { isOpen: boolean, onClose: () => void, shopId: any }) {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [project, setProject] = useState({ title: '', description: '', type: 'planned', location: '' });
  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    const formData = new FormData();
    formData.append('title', project.title);
    formData.append('description', project.description);
    formData.append('type', project.type);
    formData.append('location', project.location);
    formData.append('shop_id', shopId?.toString() || '');
    if (file) formData.append('file', file);

    try {
      const res = await fetch('https://n8n.tenear.com/webhook/political-project-add', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        alert("Project saved successfully!");
        setProject({ title: '', description: '', type: 'planned', location: '' });
        setFile(null);
        onClose();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to upload project.");
    } finally {
      setIsUploading(false);
    }
  };


  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">Add Campaign Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
            {['current', 'planned'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setProject({ ...project, type: t })}
                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                  project.type === t ? 'bg-white shadow-md text-blue-600' : 'text-gray-500'
                }`}
              >
                {t === 'current' ? 'Current (Incumbent)' : 'Planned (Future)'}
              </button>
            ))}
          </div>
          <input
            required
            className="w-full p-4 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Project Name (e.g., Ward Irrigation Kit)"
            value={project.title}
            onChange={(e) => setProject({ ...project, title: e.target.value })}
          />
          <textarea
            required
            className="w-full h-32 p-4 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Describe the project impact..."
            value={project.description}
            onChange={(e) => setProject({ ...project, description: e.target.value })}
          />
          <button
            type="submit"
            disabled={isUploading}
            className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl flex justify-center items-center gap-2 hover:bg-blue-700 disabled:opacity-50 shadow-lg"
          >
            {isUploading ? <><Loader2 className="animate-spin" /> Saving...</> : 'Publish Project'}
          </button>
        </form>
      </div>
    </div>
  );
}

// --- ANALYTICS COMPONENT ---
function Analytics({ webhookUrl, businessCategory, shopId }: { webhookUrl: string, businessCategory: string, shopId: any }) {
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: businessCategory, shop_id: shopId })
        });
        const result = await response.json();
        const rawData = Array.isArray(result) ? result : (result.data || []);

        // TRANSFORM THE DATA HERE
        const processedData = rawData.map((item: any) => ({
          ward_name: item.city || "Unknown",
          // Map sentiment_score to the bar heights
          // If score is 1, let's call it 100% supportive for now
          supportive: item.sentiment_score >= 1 ? 100 : 0,
          undecided: item.sentiment_score === 0 ? 100 : 0,
          opposed: item.sentiment_score < 0 ? 100 : 0
        }));

        setChartData(processedData);
      } catch (error) {
        console.error("Analytics fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };


    if (shopId) fetchMetrics();
  }, [shopId, webhookUrl, businessCategory]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <Loader2 className="animate-spin text-blue-600" size={40} />
      <p className="text-gray-500 animate-pulse">Fetching latest voter metrics...</p>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="h-80 flex items-end gap-4 px-4 overflow-x-auto pb-4">
        {chartData.length > 0 ? chartData.map((item: any, i: number) => (
          <div key={i} className="flex-1 min-w-[80px] flex flex-col items-center gap-3 group">
            <div className="w-full flex flex-col-reverse rounded-xl overflow-hidden relative h-full bg-gray-50 shadow-inner">
              {/* Supportive (Green) */}
              <div 
                className="bg-emerald-500 w-full transition-all duration-700 ease-out hover:brightness-110" 
                style={{ height: `${item.supportive || 0}%` }}
              />
              {/* Undecided (Orange) */}
              <div 
                className="bg-amber-400 w-full transition-all duration-700 ease-out hover:brightness-110" 
                style={{ height: `${item.undecided || 0}%` }}
              />
              {/* Opposed (Purple) */}
              <div 
                className="bg-indigo-600 w-full transition-all duration-700 ease-out hover:brightness-110" 
                style={{ height: `${item.opposed || 0}%` }}
              />
            </div>
            <span className="text-[11px] font-bold text-gray-600 truncate w-full text-center">
              {item.ward_name || `Ward ${i+1}`}
            </span>
          </div>
        )) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-2xl">
            <BarChart3 size={48} className="mb-2 opacity-20" />
            <p className="italic">No voter data received for this jurisdiction...</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 justify-center border-t border-gray-100 pt-6">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm"></div>
          <span className="text-xs font-bold text-gray-600">Supportive</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-400 shadow-sm"></div>
          <span className="text-xs font-bold text-gray-600">Undecided</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-600 shadow-sm"></div>
          <span className="text-xs font-bold text-gray-600">Opposed</span>
        </div>
      </div>
    </div>
  );
}
          


// --- MAIN DASHBOARD ---
export function PoliticalDashboard({ onViewChange }: PoliticalDashboardProps) {
  const { user } = useAuth();
  const shopId = user?.shop_id; 
  
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [branding, setBranding] = useState({ name: '', photo: null as File | null });
  const [isBrandingModalOpen, setIsBrandingModalOpen] = useState(false);
  const [profile, setProfile] = useState({ name: '', photo_url: '', primary_color: '#2563eb' });
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [replies, setReplies] = useState<{ [key: string]: string }>({});
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [qrCodeBase64, setQrCodeBase64] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [chatHistory, setChatHistory] = useState<{role: 'user' | 'ai', content: string}[]>([]);
  const [isAiThinking, setIsAiThinking] = useState(false);

  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);

  const fetchVoterMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-voter-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId })
      });
      const data = await response.json();
      setConversations(data); // Set the fetched messages to state
      setIsChatModalOpen(true); // Open the chat modal
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleAskAI = async () => {
    if (!aiQuery.trim()) return;

    const userMessage = aiQuery;
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiQuery('');
    setIsAiThinking(true);

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/political-aspirant-AI', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: userMessage, 
          shop_id: shopId,
          category: 'political' 
        })
      });

      const data = await response.json();
      // Assuming n8n returns { output: "The response text..." }
      setChatHistory(prev => [...prev, { role: 'ai', content: data.output || "I couldn't process that. Try again." }]);
    } catch (error) {
      console.error("AI Agent error:", error);
      setChatHistory(prev => [...prev, { role: 'ai', content: "Connection to AI lost. Check your network." }]);
    } finally {
      setIsAiThinking(false);
    }
  };

  const handleReply = async (msgId: string, voterPhone: string) => {
    const replyText = replies[msgId];
    if (!replyText) return;

    try {
      const res = await fetch('https://n8n.tenear.com/webhook/respond-to-voter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: msgId,
          voter_phone: voterPhone,
          reply: replyText,
          shop_id: shopId
        })
      });

      if (res.ok) {
        // 1. Update the local state to show 'replied' status immediately
        setConversations(prev => 
          prev.map(msg => msg.id === msgId ? { ...msg, status: 'replied' } : msg)
        );

        
        // Clear the input for this specific message
        setReplies(prev => ({ ...prev, [msgId]: '' }));
        // Optional: Close modal after reply
        // setIsChatModalOpen(false); 

        // 3. Close the modal after a short delay so they see the change
        setTimeout(() => {
          setIsChatModalOpen(false);
        }, 500);

      }
    } catch (err) {
      console.error("Reply error:", err);
    }
  };


  const handleConnectWhatsApp = async () => {
    setIsConnecting(true);
    setIsQRModalOpen(true);
    setQrCodeBase64(null); // Clear old QR

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-evolution-qrcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shop_id: shopId, 
          user_id: user // Ensure you have userId available in your component scope
        })
      });

      const data = await response.json();

      // Assuming n8n returns { "qrCode": "base64_string_here" }
      if (data && data.qrCode) {
        setQrCodeBase64(data.qrCode);
      } else {
        console.error("QR Code not found in response", data);
      }
    } catch (error) {
      console.error("Failed to fetch QR via n8n:", error);
    } finally {
      setIsConnecting(false);
    }
  };

  const stats = [
    { label: 'Voter Reach', value: '45.2k', icon: Users, color: 'text-blue-600', trend: '+12%' },
    { label: 'Endorsements', value: '12,840', icon: Heart, color: 'text-red-600', trend: '+5.4%' },
    { label: 'Active Ads', value: '24', icon: Megaphone, color: 'text-purple-600', trend: 'Steady' },
    { label: 'Engagement', value: '18.5%', icon: MessageSquare, color: 'text-green-600', trend: '+2.1%' },
  ];


  const quickActions = [
    { id: 'projects', title: 'Manage Projects', icon: MapPin, desc: 'Update current & planned work' },
    { id: 'manifestos', title: 'Manage Manifestos', icon: Megaphone, desc: 'Update policy points' },
    { id: 'branding', title: 'Candidate Branding', icon: UserCircle, desc: 'Profile photo & display name' },
    { id: 'analytics', title: 'Voter Insights', icon: BarChart3, desc: 'Demographic breakdown' },
    { id: 'visitor-engagement', title: 'Town Hall Chats', icon: MessageSquare, desc: 'Respond to voters' },
    { id: 'qr-generation', title: 'Rally QR Codes', icon: Share2, desc: 'Check-in at events' },
    { id: 'diaspora-hub', title: 'Diaspora Connect', icon: Globe, desc: 'Fundraising & US Town Halls' },
    { id: 'link-whatsapp', title: 'Link WhatsApp', icon: QrCode, desc: 'Scan Evolution API QR' },

    { id: 'ai-analyst', title: 'Campaign AI', icon: Bot, desc: 'Ask about voter sentiment & trends' },

  ];

  const fetchMetadata = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-politician-metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetch_metadata', shop_id: shopId })
      });
      const result = await response.json();
      const data = Array.isArray(result) ? result[0] : result;
      if (data && data.full_name) {
        setProfile({
          name: data.full_name,
          photo_url: data.photo_url,
          primary_color: '#2563eb'
        });
      }
    } catch (error) { console.error("Fetch failed:", error); } finally { setIsLoading(false); }
  };

  useEffect(() => {
    if (shopId) fetchMetadata();
  }, [shopId]);

  const handleBrandingSave = async () => {
    if (!branding.name && !branding.photo) return alert("Please provide a name or photo.");
    setIsUploading(true);
    const formData = new FormData();
    if (branding.photo) formData.append('photo', branding.photo);
    formData.append('name', branding.name);
    formData.append('shop_id', shopId?.toString() ?? "");

    try {
      await fetch('https://n8n.tenear.com/webhook/upload-metadata', {
        method: 'POST',
        body: formData
      });
      alert("Branding updated successfully!");
    } catch (error) { console.error("Upload failed", error); } finally { setIsUploading(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* 1. Header Section */}
      <div 
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl shadow-sm border-l-4 gap-4" 
        style={{ borderLeftColor: profile.primary_color }}
      >
        <div className="flex items-center gap-4">
          {profile.photo_url ? (
            <img src={profile.photo_url} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 shadow-sm" style={{ borderColor: profile.primary_color }} />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-gray-400">
              <UserCircle size={40} />
            </div>
          )}
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
              {profile.name || "Campaign Command Centre"}
            </h2>
            <p className="text-sm text-gray-500">Real-time mobilization for 2027.</p>
          </div>
        </div>
        <button 
          style={{ backgroundColor: profile.primary_color }}
          onClick={() => onViewChange('campaigns')}
          className="w-full sm:w-auto text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity shadow-md font-medium text-center"
        >
          Launch New Ad
        </button>
      </div>

      {/* 2. Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start">
              <div className={`p-2 rounded-lg bg-gray-50 ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                {stat.trend}
              </span>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Main Action Grid & Branding */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" /> Quick Actions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => {
                    if (action.id === 'projects') {
                      setIsProjectModalOpen(true);
                    } else if (action.id === 'branding') {
                      setIsBrandingModalOpen(true);
                    } else if (action.id === 'visitor-engagement') {
                      fetchVoterMessages();
                    } else if (action.id === 'link-whatsapp') {
                      handleConnectWhatsApp();
                    } else if (action.id === 'ai-analyst') {
                      setIsAIModalOpen(true);
                    } else if (action.id === 'analytics') {
                      setIsAnalyticsModalOpen(true);
                    } else {
                      onViewChange(action.id);
                    }
                  }}
                  className="flex items-start gap-3 p-4 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50 transition-all text-left group"
                >
                  <action.icon className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                  <div>
                    <p className="font-medium text-gray-900">{action.title}</p>
                    <p className="text-xs text-gray-500">{action.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Branding Section */}
          <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm bg-gradient-to-br from-white to-blue-50">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-blue-900">
              <Camera className="w-5 h-5 text-blue-600" /> Candidate Branding
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Display Name</label>
                <input 
                  type="text" 
                  placeholder="Hon. Jane Doe"
                  className="w-full border border-gray-200 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  onChange={(e) => setBranding({...branding, name: e.target.value})}
                />
              </div>
              <button 
                onClick={handleBrandingSave}
                disabled={isUploading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-md"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Branding Metadata
              </button>
            </div>
          </div>
        </div>

        <CampaignLinkGenerator shopId={user?.shop_id?.toString() ?? ""} />
      </div>

      {/* 3. Campaign Links */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <CampaignLinkGenerator shopId={user?.shop_id?.toString() ?? ""} />
      </div>

      {/* 4. Geographical Reach */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary-600" /> Constituency Hotspots
        </h3>
        <div className="rounded-lg overflow-hidden border border-gray-200">
           <ConstituencyHotspots shopId={user?.shop_id?.toString() ?? ""} />
        </div>
      </div>

      {/* --- MODALS SECTION --- */}
      
      {/* Project Modal */}
      <ProjectModal 
        isOpen={isProjectModalOpen} 
        onClose={() => setIsProjectModalOpen(false)} 
        shopId={shopId} 
      />

      {/* Branding Modal */}
      {isBrandingModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Candidate Branding</h2>
              <button onClick={() => setIsBrandingModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X />
              </button>
            </div>
          
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input 
                  type="text"
                  placeholder="e.g. Hon. Jane Doe"
                  className="w-full p-4 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setBranding({...branding, name: e.target.value})}
                />
              </div>
            
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Photo</label>
                <input 
                  type="file" 
                  accept="image/*"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={(e) => setBranding({...branding, photo: e.target.files?.[0] || null})}
                />
              </div>

              <button 
                onClick={handleBrandingSave}
                disabled={isUploading}
                className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl flex justify-center items-center gap-2 hover:bg-blue-700 disabled:opacity-50 shadow-lg"
              >
                {isUploading ? <><Loader2 className="animate-spin" /> Saving...</> : 'Update Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- TOWN HALL MODAL OVERLAY --- */}
      {isChatModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
      
            {/* Modal Header */}
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900">Voter Town Hall Chats</h2>
              <button 
                onClick={() => setIsChatModalOpen(false)} 
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content (Scrollable) */}
            <div className="p-6 overflow-y-auto space-y-4">
              {conversations.length > 0 ? (
                conversations.map((msg: any) => (
                  <div key={msg.id} className="p-4 border rounded-xl bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                        Feedback from {msg.voter_phone}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="text-gray-900 font-medium mb-4">
                      "{msg.incoming_message}"
                    </p>

                    <div className="flex gap-2">
                      <input
                        className="flex-1 p-2 border rounded-lg text-sm"
                        placeholder="Type WhatsApp reply..."
                        value={replies[msg.id] || ''}
                        onChange={(e) => setReplies({ ...replies, [msg.id]: e.target.value })}
                      />
                      <button 
                        onClick={() => handleReply(msg.id, msg.voter_phone)} 
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700"
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No voter messages found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isQRModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
            <h3 className="text-2xl font-bold mb-2">Link WhatsApp</h3>
            <p className="text-gray-500 mb-6 text-sm">
              Scan this code with your WhatsApp "Linked Devices" to start responding to voters.
            </p>
      
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-dashed border-gray-200 inline-block">
              {isConnecting ? (
                <div className="w-64 h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : qrCodeBase64 ? (
                <img src={qrCodeBase64} alt="WhatsApp QR Code" className="w-64 h-64" />
              ) : (
                <p className="text-red-500">Failed to load QR. Try again.</p>
              )}
            </div>

            <button 
              onClick={() => setIsQRModalOpen(false)}
              className="mt-8 w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {isAIModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl h-[92dvh] sm:h-[80vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex-none p-4 border-b bg-blue-600 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Bot className="w-6 h-6" />
                <h3 className="font-bold">Campaign Strategy AI</h3>
              </div>
              <button onClick={() => setIsAIModalOpen(false)} className="p-1 rounded">✕</button>
            </div>

            {/* Chat Area - UPDATED FOR SAFETY */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-gray-50">
              {(!chatHistory || chatHistory.length === 0) && (
                <div className="text-center py-10 text-gray-400">
                  <Bot className="w-12 h-12 mx-auto mb-2 opacity-20" />
                  <p>Ask me about voter engagement or 2027 trends.</p>
                </div>
              )}
  
              {/* Add the '?' after chatHistory */}
              {chatHistory?.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                    msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border shadow-sm rounded-tl-none'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
  
              {isAiThinking && (
                <div className="flex justify-start">
                  <div className="bg-white border p-3 rounded-2xl animate-pulse text-gray-400 text-xs">
                    AI is analyzing data...
                  </div>
                </div>
              )}
            </div>

            {/* Input Bar */}
            <div className="flex-none p-4 bg-white border-t">
              <input 
                type="text" 
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
                placeholder="Ask about voter sentiment..."
                className="flex-1 border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <button 
                onClick={handleAskAI}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Political Campaign Analytics Modal */}
      {isAnalyticsModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-5xl h-[85vh] rounded-[2rem] shadow-2xl p-8 overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <TrendingUp className="text-blue-600" /> 
                  Political Campaign Analytics
                </h2>
                <p className="text-sm text-gray-500">Real-time voter sentiment across active wards</p>
              </div>
              <button 
                onClick={() => setIsAnalyticsModalOpen(false)} 
                className="p-2 bg-gray-100 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <Analytics 
                webhookUrl="https://n8n.tenear.com/webhook/fetch-political-metrics"
                businessCategory="political"
                shopId={user?.shop_id} // Use the shopId available in your main component scope
              />
            </div>
      
            <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
              <button 
                onClick={() => setIsAnalyticsModalOpen(false)}
                className="px-6 py-2 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-all"
            >
                Close Insights
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ground Team Operations Grid Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <AgentIncidentTracker shopId={String(user?.shop_id || shopId || '65')} />
        <ParallelVoteTally shopId={String(user?.shop_id || shopId || '65')} />
      </div>

    </div> // This closes the main dashboard container
  ); // This ends the return statement
} // This ends the PoliticalDashboard function   

    

// --- CONSTITUENCY HOTSPOTS COMPONENT ---
const ConstituencyHotspots = ({ shopId }: { shopId: string }) => {
  const [geoData, setGeoData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [heatPoints] = useState<[number, number, number][]>([
    [-1.286, 36.817, 0.8], 
    [-1.292, 36.821, 0.5]
  ]);

  useEffect(() => {
    setIsLoading(true);
    fetch('/data/kenya_constituencies.json')
      .then(res => res.json())
      .then(data => {
        // This validation will pass smoothly now that you have real GeoJSON
        if (data && data.type === "FeatureCollection") {
          setGeoData(data);
        }
      })
      .catch(err => console.error("Map Error:", err))
      .finally(() => setIsLoading(false));
  }, []);

  // Crisp visual style to cleanly outline constituency limits
  const boundaryStyle = {
    fillColor: '#3B82F6',   // Base blue shade for regions
    fillOpacity: 0.1,      // Kept translucent so the Heatmap points remain visible underneath
    color: '#1E293B',       // Sharp Slate-800 border line for exact demarcation
    weight: 1.5,           // Clean, thin line resolution
    opacity: 0.85
  };

  const handleOnEachFeature = (feature: any, layer: any) => {
    // Extracting keys from your active dataset file
    const constituencyName = feature.properties?.CONSTITUEN || "Constituency";
    const countyName = feature.properties?.COUNTY_NAM || "County";

    // Bind the true sub-district name to the tooltip popup
    layer.bindTooltip(`<strong>${constituencyName} Constituency</strong><br/>${countyName} County`, {
      sticky: true,
      className: 'bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm text-xs font-semibold text-slate-800'
    });

    layer.on({
      mouseover: (e: any) => {
        e.target.setStyle({
          fillColor: '#10B981', // Flashes green on hover
          fillOpacity: 0.25,
          weight: 2,
          color: '#065F46'      // Crisp emerald border outline
        });
      },
      mouseout: (e: any) => {
        e.target.setStyle(boundaryStyle); // Smoothly restores back to slate border styling
      }
    });
  };


  if (isLoading) return <div className="h-[500px] flex items-center justify-center bg-gray-50 text-gray-500 font-medium">Loading Demarcation Boundaries...</div>;

  return (
    <div style={{ height: '500px', width: '100%' }} className="rounded-lg overflow-hidden border border-gray-200 relative z-0">
      <MapContainer center={[0.0236, 37.9062]} zoom={6} style={{ height: '100%', width: '100%' }}>
        <TileLayer 
          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
        />
        
        {/* Dynamic Vector Demarcation Layer */}
        {geoData && (
          <GeoJSON 
            data={geoData} 
            style={boundaryStyle} 
            onEachFeature={handleOnEachFeature} 
          />
        )}
        
        {/* Continuous Heatmap Layer representing dense voter pockets over the map vectors */}
        <HeatmapLayer points={heatPoints} />
      </MapContainer>
    </div>
  );
};
